/* eslint-disable consistent-return */
import { parse } from 'query-string'
// eslint-disable-next-line camelcase
import { decode_object } from 'rison'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import hostConf from '$config/frontend'
import { LIMITS, REGEX } from '@weblite/permission'
import { WappMode } from '@weblite-wapps/protocol'
import { getShortLinkTarget, URL_SHORTENER_PATTERN } from '$func/link-shortener'
import { showJoinHallModal } from '$comp/modals/join-hall-dialog'
import { getHallByIdentifier, getHallByInviteLink } from '$logic/hall'
import { getSubscription } from '$logic/subscription'
import { openRoom } from '$logic/opened-room'
import { showLoadFromServerLoadingModal } from '$comp/modals/loading-modal'
import { showInvalidLinkSnackbar } from '$comp/snackbars'
import { showOpenLinkConfirmModal } from '$comp/modals/confirmation-dialog'
import { isLoggedIn } from '$logic/auth'
import { handleSharedTarget } from '$logic/shared-target'
import { getPvByUserId } from '$logic/pv'
import { OverlayWappPanel, WappPanel } from '$logic/wapp-panels'
import { urlToLoadAfterLoginStorage } from '$logic/router/storage'
import { addProtocolToUrl } from '$func/link'
import { getUserByUsername } from '$logic/users'

interface UrlHandler {
  match: RegExp
  handle: (pathname: string, params: Record<string, any>) => Promise<unknown>
}

const URL_HANDLERS: UrlHandler[] = [
  {
    match: new RegExp(`/[+].{${LIMITS.inviteLinkSize}}$`),
    // '/+:inviteLink'
    handle: async pathname => {
      const inviteLink = pathname.slice(2)
      showLoadFromServerLoadingModal()
      const res = await getHallByInviteLink(inviteLink)
      await closeCurrentModal()
      if (!res?.room) return
      const { hall, room } = res
      // NOTE: If client is member of hall, redirect to hall
      if (await getSubscription(room.id)) openRoom(room.id)
      else showJoinHallModal({ hall, room, link: inviteLink })
    },
  },
  {
    match: new RegExp(`/${REGEX.username}$`),
    // '/:identifier'
    handle: async pathname => {
      const identifier = pathname.slice(1)
      showLoadFromServerLoadingModal()
      const hall = await getHallByIdentifier(identifier)
      if (hall) {
        await closeCurrentModal()
        openRoom(hall.roomId)
        return
      }
      const user = await getUserByUsername(identifier)
      if (user) {
        const pv = await getPvByUserId(user.id)
        await closeCurrentModal()
        if (pv) openRoom(pv.roomId)
        return
      }
      await closeCurrentModal()
    },
  },
  {
    match: new RegExp(`/${REGEX.username}/[0-9]+$`),
    // '/:identifier/:iid'
    handle: async pathname => {
      const [, identifier, strIid] = pathname.split('/')
      showLoadFromServerLoadingModal()
      const hall = await getHallByIdentifier(identifier)
      await closeCurrentModal()
      if (!hall) return
      openRoom(hall.roomId, +strIid)
    },
  },
  {
    match: /^\/wapp\/.+$/,
    // '/wapp/:wappId'
    handle: async (pathname, params) => {
      const [, , wappId] = pathname.split('/')

      const {
        wisId,
        input: encodedInput,
        mode,
      } = params as {
        wisId?: string
        mode?: WappMode
        input?: string
      }

      const input = decode_object(encodedInput) as Record<string, any>

      if (mode === 'main' || !mode) WappPanel.openWapp({ wappId, wisId, input })
      else if (mode === 'overlay')
        OverlayWappPanel.openWapp({ wappId, wisId, input })
    },
  },
  {
    match: /^\/_share-target\/$/,
    // '/_share-target/*'
    handle: async () => {
      handleSharedTarget()
    },
  },
  {
    match: new RegExp('^/$'),
    handle: async (_, params) => {
      if (params.roomId) openRoom(params.roomId)
      else if (params.userId) {
        const pv = await getPvByUserId(params.userId)
        openRoom(pv.roomId)
      }
    },
  },
]

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const APP_URLS_REGEXP = import.meta.env.VITE_APP_URLS_REGEXP as string
export const APP_URL_PATTERN = APP_URLS_REGEXP
  ? new RegExp(APP_URLS_REGEXP, 'gm')
  : null

export async function handleLink(link: string): Promise<void> {
  if (!link.startsWith('http')) return handleLink(addProtocolToUrl(link))

  // NOTE: Handle links of url-shortener
  if (link.match(URL_SHORTENER_PATTERN)) {
    showLoadFromServerLoadingModal()
    const target = await getShortLinkTarget(link)
    await closeCurrentModal()
    if (target) return handleLink(target)
    showInvalidLinkSnackbar()
    return
  }

  if (
    !(
      (APP_URL_PATTERN && link.match(APP_URL_PATTERN)) ||
      link.startsWith(hostConf.FRONT_HOST)
    )
  ) {
    showOpenLinkConfirmModal(link, () => window.open(link, '_blank'))
    return
  }

  if (!isLoggedIn()) {
    urlToLoadAfterLoginStorage.set(link)
    return
  }

  const { pathname, search } = new URL(link)
  const params = parse(search)

  const handler = URL_HANDLERS.find(({ match }) => match.test(pathname))

  if (handler) await handler.handle(pathname, params)
  else if (!link.startsWith(hostConf.FRONT_HOST)) window.open(link, '_blank')
}
